import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../../Translations';
import { Program, ShareBy, ShareType } from '../Stores/ShareStore/Types';
import CourseFamilyField from './CourseFamilyField';

interface Props {
  shareType: ShareType;
  onChange: (value: ShareType) => void;
  programs: ReadonlyArray<Program>;
  programShareDisabled: boolean;
  shareBy: ShareBy;
  selectedProgramFamilyId: Maybe<number>;
  onProgramFamilyChange: (programFamilyId: Maybe<number>) => void;
}

const ShareTypeSelector: React.FC<Props> = ({
  shareType,
  onChange,
  programs,
  programShareDisabled,
  shareBy,
  selectedProgramFamilyId,
  onProgramFamilyChange,
}) => {
  const handleShareTypeChange = (value: ShareType) => {
    onChange(value);
    if (value === 'collection') {
      onProgramFamilyChange(nothing());
    }
  };

  return (
    <div className="mb-8" data-intercom-target="what-do-you-want-to-share-section">
      <h3 className="font-semibold text-sm leading-5 tracking-normal text-foreground mb-4">
        <T kind="What would you like to share?" />
      </h3>

      <div className="space-y-3">
        <label className="flex items-start">
          <input
            type="radio"
            name="shareType"
            value="collection"
            checked={shareType === 'collection'}
            onChange={() => handleShareTypeChange('collection')}
            className="mt-1 mr-3 text-primary"
            data-intercom-target="share-type-collection-radio"
          />
          <div>
            <div className="font-normal text-sm leading-5 tracking-normal text-foreground">
              <T kind="Collection access (participants start by browsing the collection)" />
            </div>
          </div>
        </label>

        <label
          className={`flex items-start ${programShareDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-disabled={programShareDisabled}
        >
          <input
            type="radio"
            name="shareType"
            value="program"
            checked={shareType === 'program'}
            onChange={() => handleShareTypeChange('program')}
            disabled={programShareDisabled}
            tabIndex={programShareDisabled ? -1 : undefined}
            className="mt-1 mr-3 text-primary"
            data-intercom-target="share-type-program-radio"
          />
          <div>
            <div className="font-normal text-sm leading-5 tracking-normal text-foreground">
              <T kind="Course access (participants start in a specific course)" />
            </div>
          </div>
        </label>

        {/*
         * A URL shares one course, chosen here with the invite page's picker
         * (DVLP-8060). An email invite picks its course on the invite page.
         * Full width, so the card lines up with the URL field below it.
         */}
        {shareType === 'program' && shareBy === 'url' && programs.length > 0 && (
          <div data-intercom-target="share-program-family-select">
            <CourseFamilyField
              programs={programs}
              selectedProgramFamilyId={selectedProgramFamilyId}
              onChange={(programFamilyId) => onProgramFamilyChange(just(programFamilyId))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(ShareTypeSelector);
